// src/locales/manage-p2-en.ts
export default {
  management: {
    rights: {
      title: "Rights Management",
      description: "Manage and protect your works",
      tabs: {
        works: "Works",
        licenses: "Licenses",
        violations: "Violations",
        revenue: "Revenue",
        contracts: "Contracts",
        settings: "Settings"
      }
    },
    works: {
      title: "My Works",
      registerNew: "Register New Work",
      emptyState: {
        title: "No works yet",
        description: "You haven't registered any works yet. Start by registering your first work."
      },
      filters: {
        all: "All Types",
        image: "Image",
        audio: "Audio",
        video: "Video",
        text: "Text",
        other: "Other",
        status: {
          all: "All Statuses",
          approved: "Approved",
          pending: "Pending",
          rejected: "Rejected",
          draft: "Draft"
        }
      },
      search: "Search works...",
      noResults: "No works found",
      noResultsDesc: "No works match your current filters.",
      loading: "Loading data...",
      status: {
        approved: "Approved",
        pending: "Pending",
        rejected: "Rejected",
        draft: "Draft"
      },
      protection: {
        score: "Protection Score",
        high: "High",
        medium: "Medium",
        low: "Low"
      },
      actions: {
        view: "View Details",
        edit: "Edit",
        delete: "Delete",
        protect: "Enhance Protection",
        license: "License"
      },
      metadata: {
        title: "Update Metadata",
        subtitle: "Edit descriptive information for your work",
        notFound: "Data not found",
        sections: {
          basicInfo: "Basic Information",
          workInfo: "Work Information",
          classification: "Classification and Keywords",
          additionalInfo: "Additional Information",
          timeInfo: "Time Information",
          versionHistory: "Version History",
          changeDescription: "Change Description"
        },
        fields: {
          title: "Title",
          titlePlaceholder: "Enter work title",
          titleHelp: "A clear title helps with searching and identification",
          description: "Description",
          descriptionPlaceholder: "Describe the content and purpose of the work in detail",
          descriptionHelp: "A detailed description helps users understand your work",
          contentKind: "Content Type",
          contentRating: "Content Rating",
          category: "Category",
          industry: "Industry",
          tags: "Tags",
          tagsPlaceholder: "Add tags and press Enter",
          tagsHelp: "Adding tags helps your work be discoverable",
          language: "Language",
          status: "Status",
          statusHelp: "Status can only be changed through the approval process",
          creationDate: "Creation Date",
          registrationDate: "Registration Date",
          registrationDateHelp: "Registration date cannot be changed",
          changeLog: "Change Description",
          changeLogPlaceholder: "Briefly describe the changes you made",
          changeLogHelp: "This information will be saved in the version history"
        },
        buttons: {
          cancel: "Cancel Changes",
          save: "Save Changes",
          saving: "Saving..."
        },
        messages: {
          success: "Metadata updated successfully!",
          error: "Could not update metadata. Please try again later.",
          confirmCancel: "Are you sure you want to cancel your changes?"
        }
      },
      register: {
        title: "Register New Work",
        steps: {
          upload: "Upload",
          info: "Information",
          distribution: "Distribution",
          protection: "Protection",
          confirm: "Confirm"
        },
        fields: {
          contentKind: "Work Type",
          uploadTitle: "Upload Work",
          uploadDescription: "Drag and drop files here or click to browse",
          uploadFormats: "Supported: JPEG, PNG, GIF, MP3, MP4, PDF, DOC, TXT (max 100MB)",
          uploadButton: "Select Files",
          selectedFiles: "Selected {count} files:",
          title: "Work Title",
          description: "Description",
          descriptionPlaceholder: "Detailed description of the work",
          category: "Category",
          selectCategory: "Select category",
          tags: "Tags",
          isPublic: "Display in public catalog",
          isPublicHelp: "If selected, your work will be displayed in the public catalog and can be searched by potential buyers",
          collaborators: "Collaborators",
          collaboratorsEmpty: "No collaborators have been added.",
          collaboratorsHelp: "You will be listed as the sole creator with 100% ownership.",
          addCollaborator: "Add Collaborator",
          collaboratorName: "Name",
          collaboratorEmail: "Email",
          collaboratorShare: "Distribution Share (%)",
          totalDistribution: "Total distribution share:",
          totalDistributionError: "Total distribution share must equal 100%.",
          protection: {
            title: "Protection Measures",
            description: "Choose protection measures for your work. Protection measures help prevent unauthorized use and enhance your ownership protection.",
            watermark: "Automatic Watermark",
            watermarkDesc: "Add a digital watermark to your work to prevent unauthorized copying.",
            aiMonitoring: "AI Monitoring",
            aiMonitoringDesc: "Use AI to scan and detect copyright violations across online platforms.",
            blockchain: "Blockchain Certification",
            blockchainDesc: "Register your work on blockchain to verify ownership and authenticity.",
            score: "Estimated Protection Score",
            scoreHelp: "Protection score indicates how well your work is protected. Higher score means better protection."
          },
          confirmation: {
            title: "All information is ready!",
            description: "Please review your work information and click \"Register\" to complete.",
            workInfo: "Work Information",
            collaboratorInfo: "Collaborators",
            protectionInfo: "Protection Measures",
            terms: "I agree to the Terms and Conditions",
            ownership: "I confirm that I have the right to register copyright for this work",
            on: "On",
            off: "Off"
          }
        },
        buttons: {
          back: "Back",
          continue: "Continue",
          register: "Register Work",
          processing: "Processing..."
        },
        success: {
          title: "Registration Successful!",
          description: "Your work has been registered successfully and is pending approval.",
          viewAll: "View All Works",
          registerAnother: "Register Another Work"
        }
      },
      details: {
        title: "Work Details",
        backToList: "Back to List",
        loading: "Loading work details...",
        error: "Work not found with ID: {id}",
        actions: {
          edit: "Edit",
          download: "Download",
          share: "Share",
          report: "Report Violation"
        },
        info: {
          title: "Work Information",
          registrationDate: "Registration Date",
          owner: "Owner",
          views: "Views",
          licenses: "Licenses",
          protectionScore: "Protection Score"
        },
        tabs: {
          licenses: "Licenses",
          violations: "Violations",
          revenue: "Revenue"
        },
        licenses: {
          empty: "No licenses have been issued for this work yet.",
          table: {
            id: "ID",
            licensee: "Licensee",
            type: "Type",
            startDate: "Start Date",
            endDate: "End Date",
            status: "Status"
          },
          status: {
            active: "Active",
            expired: "Expired"
          }
        },
        violations: {
          empty: "No violations detected for this work.",
          table: {
            id: "ID",
            platform: "Platform",
            detectedDate: "Detection Date",
            status: "Status",
            actions: "Actions"
          },
          status: {
            resolved: "Resolved",
            pending: "Processing"
          },
          actions: {
            details: "Details"
          }
        },
        revenue: {
          empty: "No revenue data available for this work yet.",
          chart: "Revenue Chart",
          table: {
            month: "Month",
            revenue: "Revenue"
          }
        },
        protection: {
          title: "Protection Status",
          overall: "Overall Protection Score",
          features: {
            content: {
              title: "Content Protection",
              description: "Watermark, identification"
            },
            monitoring: {
              title: "Violation Monitoring",
              description: "Automatic AI scanning"
            },
            blockchain: {
              title: "Blockchain Certification",
              description: "Origin verification"
            }
          },
          status: {
            active: "Active",
            inactive: "Inactive"
          },
          enhance: "Enhance Protection"
        },
        relatedWorks: {
          title: "Related Works"
        }
      }
    },
    licenses: {
      title: "License Management",
      description: "Manage licenses for your works",
      create: "Create New License",
      table: {
        id: "License ID",
        work: "Work",
        licensee: "Licensee",
        startDate: "Start Date",
        endDate: "End Date",
        remainingDays: "Remaining Days",
        value: "Value",
        status: "Status",
        actions: "Actions"
      },
      status: {
        active: "Active",
        pending: "Pending Approval",
        completed: "Completed",
        cancelled: "Cancelled"
      },
      actions: {
        view: "View Details",
        download: "Download License",
        renew: "Renew"
      },
      summary: {
        title: "License Summary",
        total: "Total Licenses:",
        active: "Active:",
        pending: "Pending:",
        completed: "Completed:"
      },
      expiring: {
        title: "Expiring Soon",
        empty: "No licenses are expiring soon",
        days: "{days} days left"
      },
      value: {
        title: "License Value",
        total: "Total Value:",
        active: "Active Licenses:",
        completed: "Completed Licenses:"
      },
      filters: {
        search: "Search by work name, author...",
        status: "Status"
      }
    },
    contracts: {
      title: "Contract Management",
      description: "View and manage copyright licensing contracts",
      search: "Search by work title, author...",
      filters: "Filters",
      export: "Export",
      loading: "Loading data...",
      empty: "No contracts found",
      table: {
        id: "Contract ID",
        work: "Work",
        owner: "Owner",
        startDate: "Start Date",
        endDate: "End Date",
        remainingDays: "Remaining Time",
        value: "Value",
        status: "Status",
        actions: "Actions"
      },
      status: {
        active: "Active",
        pendingApproval: "Pending Approval",
        completed: "Completed",
        cancelled: "Cancelled"
      },
      actions: {
        view: "View Details",
        download: "Download Contract",
        renew: "Renew"
      },
      summary: {
        title: "Contract Summary",
        total: "Total Contracts:",
        active: "Active:",
        pending: "Pending Approval:",
        completed: "Completed:"
      },
      expiring: {
        title: "Expiring Soon",
        empty: "No contracts expiring soon",
        days: "{days} days remaining"
      },
      value: {
        title: "Contract Value",
        total: "Total Value:",
        active: "Active Contracts:",
        completed: "Completed Contracts:"
      }
    },
    violations: {
      title: "Violation Warnings",
      description: "Manage and send warnings to copyright infringers",
      create: "Create New Warning",
      search: "Search by title or URL...",
      filters: {
        status: {
          all: "All Statuses",
          pending: "Pending",
          sent: "Sent",
          acknowledged: "Acknowledged",
          ignored: "Ignored"
        }
      },
      empty: "No violation warnings found.",
      loading: "Loading data...",
      showing: "Showing {shown} of {total} warnings",
      table: {
        work: "Work",
        platform: "Platform",
        detected: "Detected",
        severity: "Severity",
        status: "Status",
        type: "Violation Type",
        actions: "Actions"
      },
      severity: {
        critical: "Critical",
        high: "High",
        medium: "Medium",
        low: "Low"
      },
      status: {
        pending: "Pending",
        sent: "Sent",
        acknowledged: "Acknowledged",
        ignored: "Ignored"
      },
      types: {
        unauthorized_usage: "Unauthorized Usage",
        deepfake: "Deepfake",
        impersonation: "Impersonation",
        false_information: "False Information",
        inappropriate_content: "Inappropriate Content",
        other: "Other"
      },
      actions: {
        view: "View",
        send: "Send",
        block: "Block"
      }
    },
    reportViolation: {
      title: "Report Copyright Violation",
      description: "Report cases of unauthorized use of your work",
      form: {
        workId: {
          label: "Violated Work",
          placeholder: "Select work"
        },
        violationType: {
          label: "Violation Type",
          placeholder: "Select violation type",
          options: {
            unauthorized_usage: "Unauthorized Usage",
            impersonation: "Impersonation",
            inappropriate_content: "Inappropriate Content",
            false_information: "False Information",
            other: "Other"
          }
        },
        violationUrl: {
          label: "Violation URL",
          placeholder: "https://example.com/violation-page"
        },
        platform: {
          label: "Platform",
          placeholder: "Select platform",
          options: {
            facebook: "Facebook",
            instagram: "Instagram",
            youtube: "YouTube",
            tiktok: "TikTok",
            website: "Website",
            other: "Other"
          }
        },
        description: {
          label: "Violation Details",
          placeholder: "Describe the violation in detail, including how you discovered it, when it was detected, and its impact on you."
        },
        evidence: {
          label: "Violation Evidence",
          text: "Drag and drop evidence images or files here, or click to select from your computer",
          help: "You can attach screenshots, videos, or other documents to prove the violation. Maximum 5 files, each not exceeding 10MB."
        },
        declaration: {
          label: "I declare that the information provided is accurate and I am the legal owner of this work."
        },
        buttons: {
          cancel: "Cancel",
          submit: "Submit Report",
          submitting: "Submitting..."
        }
      },
      steps: {
        title: "Violation Reporting Process",
        submit: "Submit Report",
        submitDesc: "User submits a violation report with detailed information",
        review: "Review and Assessment",
        reviewDesc: "Moderation team reviews and assesses the report",
        notify: "Notification and Takedown Request",
        notifyDesc: "Notify the violator and request content removal",
        process: "Processing and Update",
        processDesc: "Final processing and update report status"
      },
      tips: {
        title: "Violation Reporting Guide",
        evidence: {
          title: "Prepare Evidence",
          description: "Collect screenshots, videos, or documents proving the violation. Clear evidence helps expedite the processing."
        },
        url: {
          title: "Provide Exact URL",
          description: "URL should lead directly to the violating content. If violations appear in multiple places, report each URL separately."
        },
        timeline: {
          title: "Processing Time",
          description: "Reports are typically reviewed within 24-48 hours. Serious violations are prioritized."
        }
      },
      messages: {
        success: {
          title: "Violation report submitted successfully!",
          description: "We have received your report and will review it as soon as possible. You will be notified when there's an update on the processing status."
        },
        error: {
          title: "An error occurred!",
          description: "Could not submit violation report. Please try again later."
        }
      }
    },
    ViolationDetail: {
      title: "Violation Report Details #{id}",
      backToList: "Back to List",
      loading: "Loading violation report details...",
      error: {
        title: "An error occurred",
        notFound: "Could not find violation report with ID: {id}"
      },
      info: {
        title: "Violation Information",
        work: "Violated Work",
        type: "Violation Type",
        url: "Violation URL",
        platform: "Platform",
        detected: "Detection Date",
        description: "Detailed Description"
      },
      evidence: {
        title: "Attached Evidence",
        download: "Download"
      },
      status: {
        title: "Processing Status",
        current: "Current Status",
        history: "Status History",
        nextSteps: "Next Steps"
      },
      actions: {
        title: "Actions",
        update: "Update Report",
        cancel: "Cancel Report"
      },
      statusValues: {
        pending: "Pending",
        investigating: "Investigating",
        resolved: "Resolved",
        rejected: "Rejected"
      }
    },
    revenue: {
      title: "Revenue Statistics",
      export: "Export Report",
      period: {
        label: "Time Period:",
        week: "This Week",
        month: "This Month",
        quarter: "This Quarter",
        year: "This Year",
        custom: "Custom",
        from: "From",
        to: "To",
        current: "Current data:"
      },
      summary: {
        total: "Total Revenue",
        license: "License Revenue",
        usage: "Usage Revenue",
        growth: "+{percent}% from previous period",
        percent: "{percent}% of total revenue"
      },
      tabs: {
        overview: "Overview",
        works: "Revenue by Work"
      },
      charts: {
        revenue: "Revenue Chart",
        revenuePlatform: "Revenue by Platform"
      },
      transactions: {
        title: "Transaction History",
        table: {
          id: "ID",
          work: "Work",
          licensee: "Licensee",
          amount: "Amount",
          date: "Date"
        }
      },
      nextPayment: {
        title: "Next Payment",
        date: "Payment Date",
        withdraw: "Withdraw"
      },
      worksTable: {
        work: "Work",
        revenue: "Revenue",
        growth: "Growth",
        details: "Details"
      }
    },
    payment: {
      title: "Withdraw Copyright Earnings",
      description: "Manage and withdraw funds from your copyright revenue",
      withdrawForm: {
        title: "Create Withdrawal Request",
        amount: {
          label: "Amount to withdraw",
          placeholder: "Enter amount",
          available: "Available balance:"
        },
        method: {
          label: "Payment Method",
          placeholder: "Select payment method"
        },
        addMethod: "Add New",
        submit: "Withdraw"
      },
      summary: {
        title: "Financial Overview",
        balance: "Available Balance",
        totalWithdrawn: "Total Withdrawn",
        processTime: "Processing Time",
        processTimeDesc: "2-5 business days depending on payment method"
      },
      history: {
        title: "Withdrawal History",
        viewAll: "View All",
        loading: "Loading...",
        table: {
          id: "ID",
          date: "Request Date",
          amount: "Amount",
          method: "Method",
          status: "Status"
        },
        status: {
          completed: "Completed",
          processing: "Processing"
        }
      },
      addMethod: {
        title: "Add New Payment Method",
        type: {
          label: "Method Type",
          bank: "Bank Account",
          ewallet: "E-Wallet"
        },
        name: {
          label: "Display Name",
          placeholder: "E.g., Personal VCB Bank"
        },
        bankName: {
          label: "Bank Name",
          placeholder: "E.g., Vietcombank"
        },
        accountNumber: {
          label: "Account Number",
          ewalletLabel: "Phone Number/Email"
        },
        holderName: {
          label: "Account Holder Name"
        },
        cancel: "Cancel",
        add: "Add Method"
      },
      confirmation: {
        title: "Confirm Withdrawal",
        message: "Are you sure you want to withdraw {amount} to the selected payment method?",
        note: "Processing time is typically 2-5 business days depending on the payment method.",
        cancel: "Cancel",
        confirm: "Confirm Withdrawal"
      },
      messages: {
        success: "Withdrawal request has been submitted successfully and is being processed.",
        error: "An error occurred. Please try again.",
        validation: {
          methodRequired: "Please select a payment method",
          invalidAmount: "Please enter a valid amount",
          insufficientBalance: "Withdrawal amount exceeds available balance"
        }
      }
    },
    settings: {
      title: "Account Settings",
      tabs: {
        account: "Account Information",
        security: "Security",
        notification: "Notifications",
        privacy: "Privacy",
        close: "Close Account"
      },
      account: {
        title: "Personal Information",
        firstName: "First Name",
        lastName: "Last Name",
        email: "Email",
        phone: "Phone Number",
        address: "Address",
        bio: "Bio",
        save: "Save Changes"
      },
      security: {
        title: "Account Security",
        password: {
          title: "Change Password",
          current: "Current Password",
          new: "New Password",
          confirm: "Confirm New Password",
          requirements: "Password must be at least 8 characters long, including uppercase, lowercase, and numbers."
        },
        twoFactor: {
          title: "Two-Factor Authentication",
          enable: "Enable Two-Factor Authentication",
          description: "When enabled, you will need to enter a verification code sent to your phone each time you log in."
        },
        sessions: {
          title: "Current Login Sessions",
          current: "Current",
          logout: "Log Out",
          save: "Save Changes"
        }
      },
      notification: {
        title: "Notification Settings",
        email: {
          title: "Email",
          account: "Account Updates",
          accountDesc: "Receive notifications about changes to your account",
          violations: "Copyright Violations",
          violationsDesc: "Receive notifications when copyright violations are detected",
          revenue: "Revenue and Payments",
          revenueDesc: "Receive notifications about revenue and payments",
          marketing: "Feature Updates and News",
          marketingDesc: "Receive information about new features and news from Vietrro"
        },
        push: {
          title: "Push Notifications",
          violations: "Copyright Violations",
          violationsDesc: "Receive notifications when copyright violations are detected",
          licenses: "New License Requests",
          licensesDesc: "Receive notifications when there are new license requests",
          revenue: "Revenue and Payments",
          revenueDesc: "Receive notifications about revenue and payments"
        },
        save: "Save Changes"
      },
      privacy: {
        title: "Privacy Settings",
        profile: {
          title: "Information Display",
          personal: "Show personal information on public profile",
          personalDesc: "Allow displaying your personal information on your public profile",
          works: "Show my works list",
          worksDesc: "Allow displaying your works list on your public profile",
          revenue: "Show revenue information",
          revenueDesc: "Allow displaying your revenue information"
        },
        catalog: {
          title: "Work Catalog",
          visibility: "Show works to",
          options: {
            all: "Everyone",
            registered: "Registered users only",
            licensed: "Licensed users only",
            none: "No one (Only me)"
          },
          licenseInfo: "Display license information",
          licenseInfoDesc: "Allow displaying license information for your works"
        },
        data: {
          title: "Personal Data",
          download: "Download Personal Data",
          downloadDesc: "Download all personal data that we store about you"
        },
        save: "Save Changes"
      },
      close: {
        title: "Close Account",
        warning: {
          title: "Warning!",
          description: "Closing your account will:",
          items: [
            "Delete all your personal data",
            "Cancel all active licenses",
            "Stop monitoring for copyright violations",
            "Make account recovery impossible after closure"
          ],
          note: "Please consider carefully before taking this action."
        },
        password: "Enter your password to confirm",
        reason: {
          label: "Reason for closing account",
          placeholder: "Choose reason...",
          options: {
            notNeeded: "I no longer need it",
            technical: "I'm experiencing technical issues",
            unsatisfied: "I'm not satisfied with the service",
            newAccount: "I want to create a new account",
            other: "Other reason"
          }
        },
        confirmation: "I understand that this action cannot be undone",
        submit: "Close Account"
      },
      messages: {
        success: "Information updated successfully!",
        error: "An error occurred. Please try again."
      }
    },
    catalog: {
      title: "Explore Copyright Works",
      description: "Search and purchase licenses for creative works",
      search: {
        title: "Search Catalog",
        placeholder: "Enter search keywords...",
        button: "Search"
      },
      filters: {
        title: "Filters",
        category: "Category",
        allCategories: "All Categories",
        workType: "Work Type",
        allTypes: "All Types",
        licenseType: "License Type",
        allLicenses: "All Licenses",
        priceRange: "Price Range",
        allPrices: "All Prices",
        prices: {
          under1m: "Under 1 million",
          from1mTo2m: "1 million - 2 million",
          from2mTo5m: "2 million - 5 million",
          from5mTo10m: "5 million - 10 million",
          above10m: "Above 10 million"
        },
        clearFilters: "Clear Filters"
      },
      display: {
        title: "Display:",
        grid: "Grid",
        list: "List",
        showing: "Showing {count} works",
        sort: {
          label: "Sort:",
          newest: "Newest",
          oldest: "Oldest",
          priceLow: "Price: Low to High",
          priceHigh: "Price: High to Low",
          popularity: "Most Popular",
          rating: "Highest Rated"
        }
      },
      loading: "Loading data...",
      empty: {
        title: "No matching works found",
        description: "Please try with different filters",
        clearFilters: "Clear Filters"
      },
      buyLicense: "Buy License",
      viewDetails: "View Details",
      licenseInfo: {
        title: "Information",
        standard: {
          title: "Standard License",
          description: "Allows using the work in a specific project, non-exclusive."
        },
        extended: {
          title: "Extended License",
          description: "Allows using the work in multiple projects, including commercial use."
        },
        exclusive: {
          title: "Exclusive License",
          description: "Grants exclusive usage rights, no one else can purchase a license."
        }
      },
      pagination: {
        previous: "Previous",
        next: "Next"
      }
    },
    profile: {
      account: {
        title: "Account Information",
        editProfile: "Edit Profile",
        contact: "Contact",
        newWork: "Add New Work"
      },
      protection: {
        title: "Protection Score",
        good: "Good",
        average: "Average",
        poor: "Poor",
        improve: "Improve Protection Score"
      },
      contact: {
        title: "Contact Information",
        email: "Email",
        phone: "Phone",
        address: "Address",
        joinDate: "Join Date"
      },
      tabs: {
        personal: "Personal Information",
        works: "Works",
        licenses: "Licenses",
        revenue: "Revenue"
      },
      personal: {
        intro: "Introduction",
        skills: "Skills",
        fields: "Fields of Activity"
      },
      works: {
        title: "My Works",
        add: "Add New Work",
        registrationDate: "Registration Date",
        protection: "Protection Score",
        viewAll: "View All Works",
        actions: {
          view: "View",
          edit: "Edit",
          license: "License"
        },
        status: {
          registered: "Registered",
          processing: "Processing"
        }
      },
      licenses: {
        title: "Licenses",
        manage: "Manage Licenses",
        work: "Work",
        expiry: "Expiry Date",
        viewAll: "View All Licenses",
        actions: {
          details: "Details",
          renew: "Renew"
        },
        status: {
          active: "Active"
        }
      },
      revenue: {
        title: "Revenue",
        analytics: "View Detailed Analytics",
        overview: "Revenue Overview",
        total: "Total Revenue",
        thisMonth: "This Month",
        growth: "Growth",
        transactions: "Recent Transactions",
        viewAll: "View All Transactions",
        table: {
          id: "ID",
          date: "Date",
          description: "Description",
          amount: "Amount",
          status: "Status"
        },
        status: {
          completed: "Completed",
          processing: "Processing"
        }
      }
    },
    common: {
      latestAlerts: "Latest Alerts",
      days: "{days} days",
      loading: "Loading...",
      good: "good",
      alerts: "alerts",
      issues: "issues",
      noData: "No data",
      viewAll: "View All",
      viewDetails: "View Details",
      fileInfo: "Selected {name} ({size} MB)",
      filters: {
        all: "All"
      },
      statuses: {
        active: "Active",
        inactive: "Inactive"
      },
      success: "Success",
      error: "Error",
      save: "Save",
      cancel: "Cancel",
      confirm: "Confirm",
      delete: "Delete",
      edit: "Edit",
      view: "View",
      add: "Add",
      remove: "Remove",
      search: "Search",
      filter: "Filter",
      sort: "Sort",
      select: "Select",
      next: "Next",
      back: "Back",
      submit: "Submit",
      proceed: "Proceed",
      upload: "Upload",
      download: "Download",
      share: "Share",
      more: "See More",
      yes: "Yes",
      no: "No",
      on: "On",
      off: "Off"
    },
    error: {
      notFound: "Not Found",
      backToHome: "Back to Home",
      goBack: "Go Back",
      contactSupport: "Contact Support",
      contactMessage: "If you believe this is an error, please contact us."
    }
  }
};
// ,
//       actions: {
//         view: "View Details",
//         download: "Download Contract",
//         renew: "Renew"
//       },
//       summary: {
//         title: "Contract Summary",
//         total: "Total Contracts:",
//         active: "Active:",
//         pending: "Pending Approval:",
//         completed: "Completed:"
//       },
//       expiring: {
//         title: "Expiring Soon",
//         empty: "No contracts are expiring soon",
//         days: "{days} days left"
//       },
//       value: {
//         title: "Contract Value",
//         total: "Total Value:",
//         active: "Active Contracts:",
//         completed: "Completed Contracts:"
//       }